import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { cn } from '../../../lib/utils.js';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** Largura — default 480px. */
  width?: number;
  /** Variante sheet lateral (estilo print MetricsFarma). */
  variant?: 'modal' | 'sheet';
}

export function Modal({ open, onClose, title, children, width = 480, variant = 'modal' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex bg-n-900/40',
        variant === 'modal' ? 'items-center justify-center' : 'items-stretch justify-end',
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          'bg-white shadow-lg',
          variant === 'modal'
            ? 'rounded-lg max-h-[90vh] overflow-auto'
            : 'h-screen overflow-auto',
        )}
        style={{ width: `${width}px` }}
      >
        <div className="flex items-center justify-between border-b border-n-200 px-5 py-4 sticky top-0 bg-white z-10">
          <h2 className="text-[16px] font-semibold text-n-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-n-500 hover:text-n-700 rounded-md p-1 hover:bg-n-100"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
